import { EditedUser, GetUsersUser, SortingOrder } from '@/modules/common/types';

import { getUserLabel } from './user';

export const genericSortTwoColumns = <T extends Record<string, any>>(
  array: T[],
  firstColumn: keyof T,
  secondColumn: keyof T,
  direction: keyof typeof SortingOrder = 'ASC'
): T[] => {
  return [...array].sort((a, b) => {
    const aFirst = a[firstColumn] ?? '';
    const bFirst = b[firstColumn] ?? '';
    const aSecond = a[secondColumn] ?? '';
    const bSecond = b[secondColumn] ?? '';

    const result = String(aFirst).localeCompare(String(bFirst));
    return result === 0
      ? String(aSecond).localeCompare(String(bSecond))
      : direction === 'ASC'
        ? result
        : -result;
  });
};

export const genericSortSingleColumn = <T>(
  array: T[],
  column: string,
  direction = SortingOrder.ASC
) => {
  array.sort((a: any, b: any) => {
    const result = a[column] < b[column] ? -1 : a[column] > b[column] ? 1 : 0;
    return direction === SortingOrder.ASC ? result : -result;
  });
  return array;
};

const sortThreeColumns = (
  a: any,
  b: any,
  firstColumn: string,
  secondColumn: string,
  thirdColumn: string
) => {
  const firstFiledComparison =
    typeof a[firstColumn] === 'string'
      ? a[firstColumn].localeCompare(b[firstColumn])
      : String(a[firstColumn]).localeCompare(String(b[firstColumn]));

  const secondFieldComparison =
    typeof a[secondColumn] === 'string'
      ? a[secondColumn].localeCompare(b[secondColumn])
      : String(a[secondColumn]).localeCompare(String(b[secondColumn]));

  const thirdFieldComparison =
    typeof a[thirdColumn] === 'string'
      ? a[thirdColumn].localeCompare(b[thirdColumn])
      : String(a[thirdColumn]).localeCompare(String(b[thirdColumn]));

  return firstFiledComparison || secondFieldComparison || thirdFieldComparison;
};

export const genericSortThreeColumns = <T>(
  array: T[],
  firstColumn: string,
  secondColumn: string,
  thirdColumn: string,
  direction = SortingOrder.ASC
) => {
  if (direction === SortingOrder.ASC) {
    array.sort((a: any, b: any) => sortThreeColumns(a, b, firstColumn, secondColumn, thirdColumn));
  } else {
    array.sort((a: any, b: any) => sortThreeColumns(b, a, firstColumn, secondColumn, thirdColumn));
  }
  return array;
};

export const sortUserArray = (users: GetUsersUser[]): EditedUser[] => {
  const sortedUsers: EditedUser[] = JSON.parse(JSON.stringify(users));
  sortedUsers.forEach((user) => {
    user.fullName = getUserLabel(user);
  });
  sortedUsers.sort((a, b) => a.fullName.localeCompare(b.fullName));
  return sortedUsers;
};

export const sortEsrsChaptersByLabel = (
  esrsChaptersOptions: {
    label: string;
    key: string;
  }[]
) => {
  const order: { [key in 'E' | 'S' | 'G']: number } = { E: 1, S: 2, G: 3 };
  const sortedTopics = esrsChaptersOptions?.sort((a, b) => {
    const labelA = a.label;
    const labelB = b.label;

    const [prefixA, numberA] = [labelA[0], parseInt(labelA.slice(1), 10)];
    const [prefixB, numberB] = [labelB[0], parseInt(labelB.slice(1), 10)];

    if (order[prefixA as 'E' | 'S' | 'G'] !== order[prefixB as 'E' | 'S' | 'G']) {
      return order[prefixA as 'E' | 'S' | 'G'] - order[prefixB as 'E' | 'S' | 'G'];
    }
    return (numberA || 0) - (numberB || 0);
  });

  return sortedTopics;
};
